<template>
  <div
    class="cn-category-card"
    :class="{
      [`cn-category-card--${color}`]: color,
      'cn-category-card--small': small,
    }"
  >
    <div class="cn-category-card__lines-and-boxes">
      <div class="cn-category-card__lines">
        <div class="line" />
        <div class="line" />
      </div>
      <div class="cn-category-card__boxes">
        <div class="box" />
        <div class="box" />
        <div class="box" />
      </div>
    </div>
    <div class="cn-category-card__block">
      <div class="cn-category-card__content-wrapper">
        <div class="cn-category-card__content">
          <h2 class="cn-category-card__title">
            {{ name }}
          </h2>
          <div v-if="customModelsCount" class="cn-category-card__model-count">
            {{ modelsCount }}
          </div>
          <div v-else class="cn-category-card__model-count">
            {{ modelsCount }} {{ $t('model', modelsCount) }}
          </div>
        </div>
        <CdnImg
          v-if="image"
          class="image"
          :src="image"
          format="webp"
          :width="small ? 240 : 326"
          :height="small ? 240 : 326"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CdnImg } from '@webplatform/cdn-image';
import type { Colors } from '@/composables/useBannersColors';

interface Props {
  name?: string;
  description?: string;
  color?: Colors;
  image?: string;
  position?: number;
  modelsCount?: number | string;
  small?: boolean;
  customModelsCount?: boolean;
}
withDefaults(defineProps<Props>(), {
  name: 'Banner title',
  description: 'Banner description',
  color: 'gray',
  image: '/img/watches.png',
  position: 1,
  modelsCount: 0,
  small: false,
});
</script>

<style lang="scss">
.cn-category-card {
  --ab-canyon-category-card-color-bg: var(--ab-canyon-color-neutral-light);
  --ab-canyon-category-card-color-bg-gray: var(--ab-canyon-color-neutral-light);
  --ab-canyon-category-card-color-bg-red: var(--ab-canyon-color-accent);
  --ab-canyon-category-card-color-bg-yellow: var(--ab-canyon-color-accent-2);
  --ab-canyon-category-card-color-bg-blue: var(--ab-canyon-color-primary);

  --ab-canyon-category-card-boxes-color-border: var(--ab-canyon-color-accent-2);
  --ab-canyon-category-card-boxes-color-border-gray: var(
    --ab-canyon-color-accent-2
  );
  --ab-canyon-category-card-boxes-color-border-red: var(
    --ab-canyon-color-accent-2
  );
  --ab-canyon-category-card-boxes-color-border-yellow: var(
    --ab-canyon-color-light
  );
  --ab-canyon-category-card-boxes-color-border-blue: var(
    --ab-canyon-color-accent-4
  );

  --ab-canyon-category-card-content-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-category-card-content-color-text-gray: var(
    --ab-canyon-color-dark
  );
  --ab-canyon-category-card-content-color-text-red: var(
    --ab-canyon-color-light
  );
  --ab-canyon-category-card-content-color-text-yellow: var(
    --ab-canyon-color-dark
  );
  --ab-canyon-category-card-content-color-text-blue: var(
    --ab-canyon-color-light
  );

  --ab-canyon-category-card-lines-color-bg: linear-gradient(
    189.04deg,
    #efefef 0.51%,
    #fcfcfc 89.08%
  );

  background-color: var(--ab-canyon-category-card-color-bg);
  overflow: hidden;
  height: 100%;
  position: relative;
  &:hover {
    .cn-category-card {
      &__content-wrapper {
        > img {
          transform: scaleX(1.1) scaleY(1.1);
        }
      }
    }
  }

  &__lines-and-boxes {
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    position: absolute;
  }
  &__boxes {
    position: relative;
    height: 100%;
    .box {
      border-color: var(--ab-canyon-category-card-boxes-color-border);
      &:nth-child(1) {
        border-width: 3px;
        transform: rotate(30deg) skewY(-30deg);
        position: absolute;
      }
      &:nth-child(2) {
        border-width: 3px;
        transform: rotate(30deg) skewY(-30deg);
        position: absolute;
      }
      &:nth-child(3) {
        border-width: 3px;
        transform: rotate(30deg) skewY(-30deg);
        position: absolute;
      }
    }
  }
  &__lines {
    justify-content: flex-end;
    transform: translate(-350px, 0);
    height: 100%;
    display: flex;
    position: absolute;
    .line {
      background: var(--ab-canyon-category-card-lines-color-bg);
      &:nth-child(1) {
        transform: rotate(210deg) skewY(-30deg) scaleX(1.16) scaleY(1.16);
        width: 600px;
        height: 100%;
      }
      &:nth-child(2) {
        transform: rotate(30deg) skewY(-30deg) scaleX(1.16) scaleY(1.16);
        width: 500px;
        height: 100%;
      }
    }
  }
  &__block {
    position: relative;
    height: 100%;
  }
  &__content-wrapper {
    justify-content: space-between;
    flex-direction: column;
    height: 100%;
    display: flex;
    @media (min-width: 656px) {
      flex-direction: row;
    }
    > img {
      transition-duration: 1000ms;
      transition-property: transform;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      object-position: center;
      object-fit: contain;
      align-self: center;
      flex-shrink: 0;
      aspect-ratio: 1 / 1;
    }
  }
  &__content {
    color: var(--ab-canyon-category-card-content-color-text);

    padding-top: var(--size-xl);
    padding-bottom: var(--size-xl);
    padding-left: var(--size-3xl);
    padding-right: var(--size-3xl);
    @media (min-width: 656px) {
      padding-top: var(--size-3xl);
      padding-bottom: var(--size-3xl);
      padding-left: var(--size-4xl);
      padding-right: var(--size-4xl);
    }
  }
  &__title {
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1.75rem;
    @media (min-width: 992px) {
      line-height: 1;
    }
    @media (min-width: 1656px) {
      font-size: 1.5rem;
      line-height: 2rem;
    }
  }
  &__model-count {
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
    margin-top: calc(var(--size-base) / 4);
    @media (min-width: 1656px) {
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }
  &--gray {
    --ab-canyon-category-card-color-bg: var(
      --ab-canyon-category-card-color-bg-gray
    );
    --ab-canyon-category-card-boxes-color-border: var(
      --ab-canyon-category-card-boxes-color-border-gray
    );
    --ab-canyon-category-card-content-color-text: var(
      --ab-canyon-category-card-content-color-text-gray
    );
    --ab-canyon-category-card-lines-color-bg: var(--ab-canyon-line-bg-gray);
  }
  &--red {
    --ab-canyon-category-card-color-bg: var(
      --ab-canyon-category-card-color-bg-red
    );
    --ab-canyon-category-card-boxes-color-border: var(
      --ab-canyon-category-card-boxes-color-border-red
    );
    --ab-canyon-category-card-content-color-text: var(
      --ab-canyon-category-card-content-color-text-red
    );
    --ab-canyon-category-card-lines-color-bg: var(--ab-canyon-line-bg-red);
  }
  &--yellow {
    --ab-canyon-category-card-color-bg: var(
      --ab-canyon-category-card-color-bg-yellow
    );
    --ab-canyon-category-card-boxes-color-border: var(
      --ab-canyon-category-card-boxes-color-border-yellow
    );
    --ab-canyon-category-card-content-color-text: var(
      --ab-canyon-category-card-content-color-text-yellow
    );
    --ab-canyon-category-card-lines-color-bg: var(--ab-canyon-line-bg-yellow);
  }
  &--blue {
    --ab-canyon-category-card-color-bg: var(
      --ab-canyon-category-card-color-bg-blue
    );
    --ab-canyon-category-card-boxes-color-border: var(
      --ab-canyon-category-card-boxes-color-border-blue
    );
    --ab-canyon-category-card-content-color-text: var(
      --ab-canyon-category-card-content-color-text-blue
    );
    --ab-canyon-category-card-lines-color-bg: var(--ab-canyon-line-bg-blue);
  }
  &--small {
    .cn-category-card {
      &__boxes {
        .box {
          &:nth-child(1) {
            width: 60px;
            height: 100px;
            left: 10px;
            bottom: 40px;
          }
          &:nth-child(2) {
            width: 100px;
            height: 120px;
            top: 100px;
            right: -75px;
          }
          &:nth-child(3) {
            display: none;
          }
        }
      }
      &__content-wrapper {
        @media (min-width: 656px) {
          flex-direction: column;
        }
      }
    }
  }
  &:not(.cn-category-card--small) {
    .cn-category-card {
      &__boxes {
        .box {
          &:nth-child(1) {
            width: 60px;
            height: 80px;
            top: -10px;
            right: 90px;
          }
          &:nth-child(2) {
            width: 60px;
            height: 80px;
            left: 340px;
            bottom: -30px;
          }
          &:nth-child(3) {
            width: 120px;
            height: 180px;
            top: 170px;
            left: 80px;
          }
        }
      }
      &__content-wrapper {
        > img {
          width: 240px;
          @media (min-width: 656px) {
            width: 50%;
          }
        }
      }
    }
  }
}
</style>
